import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import { COACH_HOME, PLAYER_HOME } from "./lib/homePath";
import OnboardingGate from "./components/OnboardingGate";
import { OnboardingProvider } from "./context/OnboardingContext";
import CoachSetupGate from "./components/CoachSetupGate";
import { CoachSetupProvider } from "./context/CoachSetupContext";
import Layout from "./components/Layout";
import { ScrollToTop } from "./components/ScrollToTop";
import { UsageTracker } from "./components/UsageTracker";
import ToastMotion, { useToastRestsAtBottom } from "./components/ToastMotion";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import SignUpNew from "./pages/SignUpNew";
import FrontDoorNew from "./pages/FrontDoorNew";
import SignInNew from "./pages/SignInNew";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ForgotPasswordNew from "./pages/ForgotPasswordNew";
import ResetPasswordNew from "./pages/ResetPasswordNew";
import SignInLinkRequest from "./pages/SignInLinkRequest";
import SignInLinkCallback from "./pages/SignInLinkCallback";
import SignInLinkRequestNew from "./pages/SignInLinkRequestNew";
import SignInLinkCallbackNew from "./pages/SignInLinkCallbackNew";
import PrivacyNotice from "./pages/PrivacyNotice";
import TermsOfUse from "./pages/TermsOfUse";
import Dashboard from "./pages/Dashboard";
import Onboarding from "./pages/Onboarding";
import OnboardingNew from "./pages/OnboardingNew";
import DevelopmentPlan from "./pages/DevelopmentPlan";
import PerformanceTracking from "./pages/PerformanceTracking";
import Statistics from "./pages/Statistics";
import RecordDashboardScreen from "./components/RecordDashboardScreen";
import CareerDashboard from "./pages/CareerDashboard";
import GroundsMap from "./pages/GroundsMap";
// Issue #765 / #800 — the shared constant `/grounds` redirects into,
// rather than a hardcoded literal (see that constant's own header).
import { GROUNDS_MAP_PATH } from "./lib/groundsNearYou";
import CoachingInsights from "./pages/CoachingInsights";
import LearningHub from "./pages/LearningHub";
import GuidePage from "./pages/GuidePage";
import LearnReview from "./pages/LearnReview";
import AdminHome from "./pages/AdminHome";
import AdminGuides from "./pages/AdminGuides";
import AdminUsers from "./pages/AdminUsers";
import AdminGroundDuplicates from "./pages/AdminGroundDuplicates";
import AdminVenues from "./pages/AdminVenues";
import AdminErrors from "./pages/AdminErrors";
import CoachPortal from "./pages/CoachPortal";
import CoachPlayerRecord from "./pages/CoachPlayerRecord";
import CoachSetUp from "./pages/CoachSetUp";
import Tour from "./pages/Tour";
import CoachTeam from "./pages/CoachTeam";
import CoachGames from "./pages/CoachGames";
import CoachScheduleGame from "./pages/CoachScheduleGame";
import Settings from "./pages/Settings";
import DeleteAccountPage from "./pages/DeleteAccountPage";
import ChoosePlanPage from "./pages/ChoosePlanPage";
import PaymentReturnPage from "./pages/PaymentReturnPage";
import PaymentFailedPage from "./pages/PaymentFailedPage";
import AccountDeletedPage from "./pages/AccountDeletedPage";
import { AI_COACH_ENABLED, newUi } from "./lib/featureFlags";
import { Toaster } from "sonner";

// Issue #1079 (AppStates.dc.html `toast-success|error|info`, D118): a toast is
// one white card, an icon that says which kind, ink text. Green, red and blue
// are not in the tokens, so `richColors` is gone and index.css paints the card
// from `--nw-*`. The three glyphs are the drawn ones: success and info navy,
// error the ink warning triangle.
const TOAST_GLYPH = {
  width: 18,
  height: 18,
  viewBox: "0 0 24 24",
  fill: "none",
  strokeWidth: 2.2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
};
const TOAST_ICONS = {
  success: (
    <svg {...TOAST_GLYPH} stroke="var(--nw-navy)"><path d="M20 6L9 17l-5-5" /></svg>
  ),
  error: (
    <svg {...TOAST_GLYPH} stroke="var(--nw-ink)">
      <path d="M10.3 3.9L1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
      <path d="M12 9v4M12 17h.01" />
    </svg>
  ),
  close: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  ),
  info: (
    <svg {...TOAST_GLYPH} stroke="var(--nw-navy)"><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></svg>
  ),
};

// Issue #1079 follow-up (main red after #1101): below sonner's 600px mobile
// breakpoint the toast is held under the sticky topbar, not over it. #1101's
// drawn close button is the one control in the toast that takes pointer
// events (index.css), and it sits at the card's trailing edge, i.e. directly
// over the topbar's account control, the only sign-out below md (#262).
// The new look's topbar is 54px tall, so 80px sits just under it (AppStates
// draws the toast "under the top bar"). The old look adds a 47px nav strip
// below its 73px header; index.css lifts the toast to 128px where that strip
// renders (`.nw-mobile-strip`). The close can still sit over page content;
// that is the cost of a tappable close, and the harness requires it not to
// be a link, button or field.
const TOAST_MOBILE_OFFSET = { top: 80 };
// Issue #1384 (D367.14): from the md breakpoint (768px, #1428 moved it from 601px) the toast rests at the
// bottom-right of the viewport (24px in), app-wide (the old look too: one shared toaster), the phone convention,
// never over the top bar, the ribbon, a demo banner or a section strip. This replaces #1271's and #1304's
// measured top clearance. Below 768px it keeps the top hold: `mobileOffset` above for 600px and below (sonner's
// own phone mode) and this offset for 601px to 767px, where sonner is not in phone mode and would otherwise
// rest the card at y=24 over the top bar.
const TOAST_BOTTOM_OFFSET = { bottom: 24, right: 24 };
const TOAST_TOP_OFFSET = { top: 80 };

function App() {
  const toastAtBottom = useToastRestsAtBottom();
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <UsageTracker />
        <ToastMotion />
        {/* Issue #327. `nw-toaster` is not decoration: it is the hook
            index.css needs to out-specify two of sonner's own rules on a
            phone — the one that makes this list 16px wider than the screen,
            and the pointer-events that let a tap park a toast on the topbar
            for good. #327 rejected moving the toast to the bottom on a phone (it covers the
            primary CTA), so below 768px keeps the top hold; the top offset under the top bar is #1132's, because
            #1079's close is the one part of the toast that takes touches. */}
        <Toaster className="nw-toaster" position={toastAtBottom ? "bottom-right" : "top-right"} icons={TOAST_ICONS} closeButton visibleToasts={1} offset={toastAtBottom ? TOAST_BOTTOM_OFFSET : TOAST_TOP_OFFSET} mobileOffset={TOAST_MOBILE_OFFSET} toastOptions={{ classNames: { toast: "nw-toast", closeButton: "nw-toast-close" } }} />
        <Routes>
          {/* Issue #228: `/` is the product's front door, and it is PUBLIC.
              It used to be the dashboard's index route inside the protected
              layout below, so an unauthenticated visitor — the only kind a
              shared link produces — was bounced to /login and the entire
              explanation of the product was the sign-in page's tagline.

              The signed-in home moved to `/dashboard` rather than making `/`
              render two different things, so that the responsive harness's
              static route enumeration (frontend/e2e/routes.js) still reports
              one truthful auth axis per path: `/` public/anonymous, the
              dashboard authenticated. `Landing` itself redirects a signed-in
              visitor on to /dashboard, which is what keeps every existing
              `to="/"` / `goto("/")` landing where it always did. */}
          {/* Issue #874, slice S1: the front door and sign-in redrawn to
              FrontDoor.dc.html/SignIn.dc.html (design-baseline-round-5),
              behind newUi("signin"). Landing.jsx/Login.jsx — the shipped
              screens — are untouched and keep serving these routes with
              the flag off. */}
          <Route path="/" element={newUi("signin") ? <FrontDoorNew /> : <Landing />} />
          <Route path="/login" element={newUi("signin") ? <SignInNew /> : <Login />} />
          {/* Issue #753: the redrawn sign-up screen, behind newUi("signup")
              (epic #749). Register.jsx — the shipped screen an API test
              reads by path (api/test/guardianConfirmation.test.ts) — is
              untouched and keeps serving this route with the flag off. */}
          <Route path="/register" element={newUi("signup") ? <SignUpNew /> : <Register />} />
          {/* Issue #874 slice S3: redrawn behind newUi("signin")
              (S1/S2's own flag — this slice adds no new one). */}
          <Route path="/forgot-password" element={newUi("signin") ? <ForgotPasswordNew /> : <ForgotPassword />} />
          <Route path="/reset-password" element={newUi("signin") ? <ResetPasswordNew /> : <ResetPassword />} />
          {/* Issue #510: magic-link — the request page and the exchange
              callback the emailed link lands on. Both public, like the
              reset pair above. Issue #874 slice S2: redrawn behind
              newUi("signin") (S1's own flag — this slice adds no new one). */}
          <Route path="/sign-in-link" element={newUi("signin") ? <SignInLinkRequestNew /> : <SignInLinkRequest />} />
          <Route path="/sign-in-link/callback" element={newUi("signin") ? <SignInLinkCallbackNew /> : <SignInLinkCallback />} />
          {/* Issue #449 (interim, partial): the privacy notice. PUBLIC, like
              the routes above it — a parent deciding whether to let their
              child sign up should be able to read it before an account
              exists, which is the whole reason it is not folded into
              Settings alone. See `pages/PrivacyNotice.jsx` and
              `lib/privacyNotice.js` for what this page is and is not. */}
          <Route path="/privacy" element={<PrivacyNotice />} />
          {/* Issue #1340 (D367.3): the terms of use. PUBLIC, like /privacy --
              a parent should be able to read them before an account exists.
              One page for both looks (the /account-deleted precedent). It
              redirects to / while any placeholder remains in
              `lib/termsOfUse.js`; see `pages/TermsOfUse.jsx`. */}
          <Route path="/terms" element={<TermsOfUse />} />
          {/* Issue #1032: where a deleted account lands. PUBLIC and outside the
              shell like the sign-in pages: the session is already gone, so
              nothing here may need one. Always the same page; it reveals
              nothing. */}
          <Route path="/account-deleted" element={<AccountDeletedPage />} />
          {/* Issue #289: the first-run gate sits between the session check
              and the app chrome. It is INSIDE ProtectedRoute (so `user` is
              already resolved and no anonymous visitor ever triggers a
              status request) and OUTSIDE Layout (so a player who needs the
              walkthrough is redirected before any page under it mounts and
              starts its own fetches). The provider wraps the gate rather
              than the other way round because three consumers share its one
              request — the gate, the walkthrough, and the Dashboard's resume
              prompt. Both are transparent to the route enumerator
              (frontend/e2e/routes.js sees through wrapper elements on a
              grouping route) and to #257's deep links, which travel as
              `state.from` straight through both. */}
          {/* Issue #755: the coach's own first-run gate composes beside
              #289's, same shape, same reason — inside ProtectedRoute (user
              resolved), outside Layout (redirected before any page under
              it mounts). CoachSetupGate no-ops for anyone who isn't a
              coach (see its own header), so nesting it here costs a
              player or admin nothing. */}
          <Route element={<ProtectedRoute><OnboardingProvider><OnboardingGate><CoachSetupProvider><CoachSetupGate><Layout /></CoachSetupGate></CoachSetupProvider></OnboardingGate></OnboardingProvider></ProtectedRoute>}>
            {/* Issue #384 item 3 (Andreas's decision, 2026-08-20): the
                coach Dashboard is HIDDEN, not deleted. Martin judged it thin
                enough to be noise until the coach side grows, so a coach
                lands on My Players instead (`lib/homePath.js`) — but
                `CoachDashboard` in Dashboard.jsx is untouched, and undoing
                this is putting "coach" back in the array below.

                A `roles` array rather than a redirect inside the page, for
                the reason /statistics and /onboarding already use one
                (#283/#289): the responsive harness enumerates from this
                tree, so the coach cells are reported as role-skipped instead
                of the harness measuring a bounce. It is also the single
                backstop for every OTHER path into here — the logo links, the
                pure <Navigate> routes below (/career, /coaching-insights
                while the flag is off), a bookmark, a link Martin sends. Each
                of those still names /dashboard; a coach following one is
                redirected on by `ProtectedRoute`, which sends them to their
                own home rather than looping back here. */}
            <Route path="/dashboard" element={
              <ProtectedRoute roles={["player", "admin"]}><Dashboard /></ProtectedRoute>
            } />
            {/* Player-only in the router for the same reason /statistics is
                (#283): every read behind it assumes a `player_profiles` row,
                which no coach or admin has. `roles` rather than a redirect
                inside the page, so the harness enumerates the coach/admin
                cells as role-skipped instead of measuring a bounce. This is
                slice 4 — a coach must never be walked through a player's
                profile and a player's Identity & Vision. */}
            <Route path="/onboarding" element={
              <ProtectedRoute roles={["player"]}>{newUi("setup") ? <OnboardingNew /> : <Onboarding />}</ProtectedRoute>
            } />
            {/* Issue #338 — Martin's decision 4 on #287: ONE account page.
                `/profile` used to render Profile.jsx, which dispatched on
                role (a player's cricket profile; an account view for a
                coach/admin) — #318's "both doors, no redirect" IA. Two
                pages about "your account" meant two places to look and a
                live risk of divergent read gates, with #287's health card
                about to land on one of them. Settings absorbed all of it,
                Profile.jsx is deleted, and this is what is left.

                A LITERAL <Navigate>, not a component that forwards the
                query and hash. Two reasons, and the second is the load-
                bearing one: nothing in this app has ever produced a
                `/profile?…` link (the in-page anchor #338 introduced is
                `/settings#player-profile`), and `e2e/routes.js` drops a
                route whose element is an unconditional <Navigate> from the
                responsive matrix precisely because a redirect adds no
                unique surface. A `<ProfileRedirect/>` reading useLocation()
                would be enumerated as a page, then skip its own 15 cells
                every run as "redirected to /settings" — 15 standing skips
                to buy a query string nobody sends.

                The path STAYS DECLARED, which is what keeps a deep link
                working: `lib/redirectTarget.js` allowlists the paths this
                file declares (its companion test fails if the two drift),
                so a signed-out visitor sent to `/profile` is bounced to
                /login, comes back to `/profile`, and lands on Settings —
                rather than falling back to the dashboard, which is #257's
                own symptom. */}
            <Route path="/profile" element={<Navigate to="/settings" replace />} />
            {/* Issue #1166 (D215.1, D215.2): the plan is a player's. A coach
                opening it lands on Your players, an admin on Every player
                (both are `/coach-portal`), so the shipped coach and admin
                Development page is gone. The path STAYS DECLARED, so
                `lib/redirectTarget.js`'s allowlist stays true and a deep link
                still resolves after sign-in. `bounceTo` because an admin's
                own home is /dashboard, which is not where D215 sends them. */}
            <Route path="/development" element={
              <ProtectedRoute roles={["player"]} bounceTo={COACH_HOME}><DevelopmentPlan /></ProtectedRoute>
            } />
            {/* Issue #719 half 1: the role restriction was real but INVISIBLE
                from here — `/performance` carried no guard and
                `PerformanceTracking.jsx` returned a <Navigate> for a coach
                from inside its own body, so reading this file said the page
                was open to every role. It is the same restriction
                /statistics and /onboarding above already state in the
                router, written the same way, for the same two reasons: the
                responsive harness enumerates the coach cell as role-skipped
                instead of measuring a bounce, and this is the single
                backstop for every other path in (the nav entry, #79's
                `/performance?new=1` hand-off, a bookmark, a link Martin
                sends). The destination is unchanged — ProtectedRoute sends
                a bounced role to `homePathForRole`, which is what the page
                called. Issue #1166 (D215.2) took `admin` out of the array:
                an admin opening this page lands on Every player
                (`/coach-portal`) like /development above, so the shipped
                admin body is unreachable. The page is a player's alone. */}
            <Route path="/performance" element={
              <ProtectedRoute roles={["player"]} bounceTo={COACH_HOME}><PerformanceTracking /></ProtectedRoute>
            } />
            {/* Issue #283, finding 1: player-only in fact, so player-only in
                the router. Every read on this page assumes the caller owns a
                `player_profiles` row, which no coach or admin has — so a
                coach opening it got the load-FAILURE branch ("we just
                couldn't reach the server. Try again."), a connection error
                claimed for a structural condition, with a retry that could
                never work. Nothing here is coach-facing to redesign; the
                honest answer is the one `/performance` already gives, which
                is not to route them here at all. `roles` (rather than a
                redirect inside Statistics.jsx) so the responsive harness
                enumerates the coach/admin cells as role-skipped instead of
                walking them into a redirect. */}
            {/* Issue #762, ruling 9 — Decision 11 (2026-09-18): "the record
                absorbs My Stats". A deleted route cannot be flag-gated
                cleanly (a tester without the flag would lose a page), so
                the ELEMENT is gated instead of the route: with
                `newUi("season-record")` on, this redirects to the season
                record on `/dashboard` (which now carries everything this
                page did, filters included); with it off, `Statistics`
                renders exactly as it always has. The route itself, the
                page, and this ternary are removed outright at the v1.0.0
                flag flip — see the follow-up issue filed for that. */}
            <Route path="/statistics" element={
              <ProtectedRoute roles={["player"]}>{newUi("season-record") ? <Navigate to="/dashboard" replace /> : <Statistics />}</ProtectedRoute>
            } />
            {/* Issue #1147 (D225.1, D228.4): the Dashboard is its own ribbon
                destination at /insights, after Record Match. Player-only in
                the router for the reason /statistics is (a coach or admin has
                no player profile: the harness lists their cells as
                role-skipped). Not behind a flag: it is a new destination, so
                there is no shipped page for a flag-off leg to fall back to,
                and /career (My Stats' old path) now lands here. */}
            <Route path="/insights" element={
              <ProtectedRoute roles={["player"]}><RecordDashboardScreen /></ProtectedRoute>
            } />
            <Route path="/career" element={<Navigate to="/insights" replace />} />
            {/* Issue #214: this redirect predates there being anywhere to
                reflect — the #6 strip left it bouncing to the dashboard.
                Reflections is a real surface again, so the path named after
                it lands there. Still a pure <Navigate>, so the responsive
                harness's route enumeration (frontend/e2e/routes.js) keeps
                excluding it as a redirect with no unique surface. */}
            <Route path="/reflections" element={<Navigate to="/development?tab=reflections" replace />} />
            {/* Issue #58: geomap restored. `/grounds` keeps its pre-strip
                redirect to the unified `/grounds-map` page (GroundsMap.jsx
                absorbed GroundsDirectory.jsx's card-grid view before the
                #6 strip — see 914cc6f~1) rather than reviving a second,
                separate directory route. */}
            <Route path="/grounds" element={<Navigate to={GROUNDS_MAP_PATH} replace />} />
            <Route path="/grounds-map" element={<GroundsMap />} />
            {/* AI coach surfaces are flagged off by default (spec §7/D6, §8.1;
                issue #56) — per-token LLM cost, budget decision pending.
                Off means these routes redirect rather than render; nothing
                is deleted, `CoachingInsights` (and the chat/insights pages
                it composes) return the moment the flag flips true. */}
            <Route
              path="/coaching-insights"
              element={AI_COACH_ENABLED ? <CoachingInsights /> : <Navigate to="/dashboard" replace />}
            />
            <Route
              path="/coach-ai"
              element={<Navigate to={AI_COACH_ENABLED ? "/coaching-insights" : "/dashboard"} replace />}
            />
            <Route
              path="/ai-insights"
              element={<Navigate to={AI_COACH_ENABLED ? "/coaching-insights" : "/dashboard"} replace />}
            />
            <Route path="/learn" element={<LearningHub />} />
            {/* Issue #219/#220 (epic #315): the reviewer's checklist is
                admin-only in the router (PR #191 §10 Q3 / issue #219's
                locked decision — reviewer role IS admin, not
                `["coach","admin"]`) so a non-admin never reaches it, and so
                the responsive harness enumerates it as an admin-only cell
                rather than walking every persona into a redirect. */}
            <Route path="/learn/review" element={
              <ProtectedRoute roles={["admin"]}><LearnReview /></ProtectedRoute>
            } />
            {/* The guide page itself carries NO role guard here — access to
                an UNPUBLISHED guide is decided server-side, per guide, by
                `GuidePage.jsx` falling back to the admin-only content route
                on a 404 (see `lib/guides.js`). Gating the ROUTE to admin
                would also block every player/coach from ever reading a
                published guide, which is the opposite of what this route
                is for. */}
            <Route path="/learn/:category/:slug" element={<GuidePage />} />
            {/* Issue #755: the coach's short first-run set-up. Same
                ternary-at-the-route pattern as #753/#754
                (newUi("coach-setup") ? <CoachSetUp /> : <Navigate .../>),
                so the shipped behaviour (straight to /coach-portal) is
                untouched with the flag off — `CoachSetupGate` above is
                what actually SENDS a coach here; this line only decides
                what renders once they arrive.

                `roles={["coach"]}`, NOT `/coach-portal`'s
                `["coach", "admin"]` (fix round F8): every action on this
                screen — both Skips and Continue — is a
                `PUT /coach-profiles/me`, and that route's own role gate
                (`isCoachOnly`, `routes/coachProfiles.ts`) 403s an admin.
                Keeping "admin" here would have let one open a page whose
                every button silently failed. An admin who lands on
                `/coach-portal` needs no first-run screen anyway — nothing
                on it is theirs to set up. */}
            <Route path="/coach-setup" element={
              <ProtectedRoute roles={["coach"]}>
                {newUi("coach-setup") ? <CoachSetUp /> : <Navigate to="/coach-portal" replace />}
              </ProtectedRoute>
            } />
            {/* Issue #1514 (D406, D407): the guided tour, one route per role. Same
                ternary-at-the-route pattern as /coach-setup, with its own flag:
                with `tour` off the route goes home, and onboarding and coach
                set-up never send anyone here (D217). `roles` is one each: the
                tour is a player's or a coach's, and an admin has none (out of scope). */}
            <Route path="/tour" element={
              <ProtectedRoute roles={["player"]}>
                {newUi("tour") ? <Tour tourRole="player" /> : <Navigate to="/dashboard" replace />}
              </ProtectedRoute>
            } />
            <Route path="/tour/coach" element={
              <ProtectedRoute roles={["coach"]}>
                {newUi("tour") ? <Tour tourRole="coach" /> : <Navigate to="/coach-portal" replace />}
              </ProtectedRoute>
            } />
            <Route path="/coach-portal" element={
              <ProtectedRoute roles={["coach", "admin"]}><CoachPortal /></ProtectedRoute>
            } />
            {/* Issue #1316 (Pick XI coach 1 of 3; D297, D302, D309) — Team
                selection, Your games and Schedule a game. Same
                ternary-at-the-route pattern as /coach-setup, and
                `roles={["coach"]}` for the same reason: the API behind them
                (`/coach-games`) answers an admin 403, so an admin who could
                open the page would meet buttons that never work. With the
                flag off all three go back to the portal. The portal's own
                Team selection entry opens `/coach-portal/team` when this
                flag is on (#1318) and stays the disabled button when it is off. */}
            <Route path="/coach-portal/team" element={
              <ProtectedRoute roles={["coach"]}>
                {newUi("pick-xi") ? <CoachTeam /> : <Navigate to="/coach-portal" replace />}
              </ProtectedRoute>
            } />
            <Route path="/coach-portal/games" element={
              <ProtectedRoute roles={["coach"]}>
                {newUi("pick-xi") ? <CoachGames /> : <Navigate to="/coach-portal" replace />}
              </ProtectedRoute>
            } />
            <Route path="/coach-portal/games/schedule" element={
              <ProtectedRoute roles={["coach"]}>
                {newUi("pick-xi") ? <CoachScheduleGame /> : <Navigate to="/coach-portal" replace />}
              </ProtectedRoute>
            } />
            {/* Issue #719 half 2 — the linked player's record, read-only.
                Same `roles` array as the portal it sits under, and the
                per-player answer behind it is the API's, not this array's:
                `/stats/summary?player_id=` and `/matches?player_id=` both
                run `resolveScope` (api/src/routes/authz.ts), which admits a
                coach only through an ACTIVE `coach_links` row and answers
                every other case with the same generic 404. The role gate
                here decides who may open the PAGE; the link decides whose
                record it can show.

                `?player=<id>` rather than a `/players/:playerId` segment,
                for the reason the page's header records: it is already the
                app's way of naming a player on a coach surface (#74, #383),
                and `e2e/route-params.js` hard-fails a parameterised route
                that has no fixed URL to visit — a player profile id is a
                ULID minted at seed time, so there is none to give. A static
                path keeps this page in the responsive matrix. */}
            <Route path="/coach-portal/record" element={
              <ProtectedRoute roles={["coach", "admin"]}><CoachPlayerRecord /></ProtectedRoute>
            } />
            {/* Issue #219 / Andreas's 2026-08-20 decision: one /admin area
                with sections (Guides now, Users when #226 lands) rather
                than a standalone /learn/review-style console page —
                relocating a section later is a `SECTIONS` entry, not a URL
                change. Both admin-only in the router for the same harness
                reason as /learn/review above. */}
            <Route path="/admin" element={
              <ProtectedRoute roles={["admin"]}><AdminHome /></ProtectedRoute>
            } />
            <Route path="/admin/guides" element={
              <ProtectedRoute roles={["admin"]}><AdminGuides /></ProtectedRoute>
            } />
            {/* Issue #226: the Users section. The provisioning panel lived
                on the admin DASHBOARD from slice 2 (when /admin did not
                exist yet); this is the section it was always headed for,
                so the dashboard no longer renders it. Admin-only in the
                router for the same harness reason as its siblings — the
                responsive matrix enumerates from this tree, so the cell
                only exists once the route does. */}
            <Route path="/admin/users" element={
              <ProtectedRoute roles={["admin"]}><AdminUsers /></ProtectedRoute>
            } />
            {/* Issue #132: the Grounds section — the retrospective
                duplicate-candidate report. A THIRD section on the pattern
                #219 set and #226 confirmed, not a fourth shape: a
                `SECTIONS` entry on AdminHome plus this route. Read-only,
                and admin-only in the router for the same harness reason as
                its siblings — the responsive matrix enumerates from this
                tree, so the cell only exists once the route does. */}
            <Route path="/admin/grounds" element={
              <ProtectedRoute roles={["admin"]}><AdminGroundDuplicates /></ProtectedRoute>
            } />
            {/* Issue #166, Martin's Q4 decision (2026-08-27): the Venues
                section — the management console the duplicate REPORT above
                deliberately is not. Edit/archive a venue, manage its
                surfaces, read its audit trail (Q5: admin-only). Same
                `SECTIONS`-entry-plus-route shape as its three siblings, and
                admin-only in the router for the same harness reason. */}
            <Route path="/admin/venues" element={
              <ProtectedRoute roles={["admin"]}><AdminVenues /></ProtectedRoute>
            } />
            {/* Issue #1191 (D234): the Errors section — the read-only lookup
                for the failed-request record. A `SECTIONS` entry on
                AdminHome plus this route, and admin-only in the router for
                the same harness reason as its siblings. */}
            <Route path="/admin/errors" element={
              <ProtectedRoute roles={["admin"]}><AdminErrors /></ProtectedRoute>
            } />
            <Route path="/settings" element={<Settings />} />
            {/* Issue #1032 (D204.2): the delete-account confirm step. Player
                and coach only, behind newUi("settings"); with the flag off
                the shipped Settings page has no such step, so this goes back
                to it. The emailed confirm link lands here with `?confirm=`. */}
            <Route path="/settings/delete-account" element={
              <ProtectedRoute roles={["player", "coach"]}>
                {newUi("settings") ? <DeleteAccountPage /> : <Navigate to="/settings" replace />}
              </ProtectedRoute>
            } />
            {/* Issue #1036 (Payments 1; D204.2, D415): the three pay steps, on Stripe
                test or live keys. Player and coach only. They have no legacy version, so
                with newUi("pay") off each path goes back to the player home (a coach is
                bounced on to theirs by ProtectedRoute) and renders nothing. The access gate is a separate switch
                (BILLING_ENFORCED) and stays off. */}
            <Route path="/plan" element={
              <ProtectedRoute roles={["player", "coach"]}>
                {newUi("pay") ? <ChoosePlanPage /> : <Navigate to={PLAYER_HOME} replace />}
              </ProtectedRoute>
            } />
            <Route path="/plan/return" element={
              <ProtectedRoute roles={["player", "coach"]}>
                {newUi("pay") ? <PaymentReturnPage /> : <Navigate to={PLAYER_HOME} replace />}
              </ProtectedRoute>
            } />
            <Route path="/plan/failed" element={
              <ProtectedRoute roles={["player", "coach"]}>
                {newUi("pay") ? <PaymentFailedPage /> : <Navigate to={PLAYER_HOME} replace />}
              </ProtectedRoute>
            } />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
